import React, {useCallback, useEffect, useMemo, useState} from "react";
import {
    Alert,
    Box,
    Card,
    CardContent,
    CircularProgress,
    Collapse,
    Grid,
    IconButton,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Tooltip,
    Typography
} from "@mui/material";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import moment from "moment";
import {useNavigate} from "react-router-dom";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import dashboardServices from "app/services/dashboard-services";
import {downloadBuffer} from "app/utils/appHelpers";
import ExcelExportButton from "app/widgets/Shared/ExcelExportButton";

// Jornada de choferes: desde que el chofer inicia su primer viaje hasta que cierra su
// ultima entrega del dia (Trips::DriverWorkdays en el backend).
//
// "Fuera de horario" es lo que la jornada se sale del horario configurado del chofer. No
// se llama "horas extra" a proposito: las legales se cuentan por semana y esto no lo es.
//
// El naranja es el mismo de "Fuera de horario" en el dashboard.
const FUERA = "#EA580C";
const FUERA_FONDO = "#FFEDD5";
const JORNADA = "#8B78D9";
const PISTA = "#EEF0F4";
const FILA_ABIERTA = "#F1EEFC";
const GRIS = "#6B7280";

// Encabezados en mayuscula y gris, como el resto de los reportes.
const encabezado = {
    textTransform: "uppercase", letterSpacing: 0.6, fontSize: 13, fontWeight: 600, color: GRIS,
    borderBottomColor: "#E5E7EB", whiteSpace: "nowrap"
};

// "0000052670" -> "52670": el codigo se rellena con ceros al crear el viaje.
const codigo = (code) => String(code || "").replace(/^0+(?=\d)/, "");

const etiqueta = (bg, color) => ({
    display: "inline-block", px: 1.25, py: 0.5, borderRadius: 1.5, bgcolor: bg, color,
    fontSize: 13, fontWeight: 600, lineHeight: 1.3, whiteSpace: "nowrap"
});

// La barra del dia va de 06:00 a 24:00.
const DIA_DESDE = 6;
const DIA_HASTA = 24;

const duracion = (segundos) => {
    if (segundos === null || segundos === undefined) return "—";
    const minutos = Math.round(segundos / 60);
    const h = Math.floor(minutos / 60);
    const m = minutos % 60;
    if (h === 0) return `${m} min`;
    return m === 0 ? `${h} h` : `${h} h ${String(m).padStart(2, "0")}`;
};

// "mié. 30 sep." -> "mié, 30 sep": moment en español ya trae los puntos de abreviatura.
const fecha = (dia) => {
    const [semana, ...resto] = moment(dia).format("ddd DD MMM").replace(/\./g, "").split(" ");
    return `${semana}, ${resto.join(" ")}`;
};

const pct = (valor) => (valor === null || valor === undefined ? "—" : `${valor > 0 ? "+" : ""}${valor}%`);

// Las horas vienen en ISO con el offset de la compania: se toma la hora tal cual, sin
// pasarla a la zona del navegador.
const hora = (iso) => (iso ? moment.parseZone(iso).format("HH:mm") : "—");

const horaDecimal = (iso) => {
    const t = moment.parseZone(iso);
    return t.hours() + t.minutes() / 60;
};

const horaTexto = (texto) => {
    if (!texto) return null;
    const [h, m] = texto.split(":").map(Number);
    return h + m / 60;
};

const posicion = (h) => Math.min(Math.max((h - DIA_DESDE) / (DIA_HASTA - DIA_DESDE), 0), 1) * 100;

const BarraDelDia = ({jornada}) => {
    if (!jornada.start_at) return null;

    const inicio = horaDecimal(jornada.start_at);
    const fin = jornada.end_at ? horaDecimal(jornada.end_at) : inicio;
    const cierreHorario = horaTexto(jornada.window?.end);
    const inicioHorario = horaTexto(jornada.window?.start);

    const tramo = (desde, hasta, color, key) => hasta > desde && (
        <Box key={key} sx={{
            position: "absolute", top: 0, bottom: 0, borderRadius: 7, bgcolor: color,
            left: `${posicion(desde)}%`, width: `${posicion(hasta) - posicion(desde)}%`, minWidth: 4
        }}/>
    );

    const dentroDesde = inicioHorario !== null ? Math.max(inicio, inicioHorario) : inicio;
    const dentroHasta = cierreHorario !== null ? Math.min(fin, cierreHorario) : fin;

    return (
        <Tooltip title={`${hora(jornada.start_at)} → ${hora(jornada.end_at)}${jornada.window ? ` · horario ${jornada.window.start}–${jornada.window.end}` : ""}`}>
            <Box sx={{position: "relative", height: 14, bgcolor: PISTA, borderRadius: 7, minWidth: 160}}>
                {inicioHorario !== null && tramo(inicio, Math.min(inicioHorario, fin), FUERA, "antes")}
                {tramo(dentroDesde, dentroHasta, JORNADA, "dentro")}
                {cierreHorario !== null && tramo(Math.max(cierreHorario, inicio), fin, FUERA, "despues")}
                {cierreHorario !== null && (
                    <Box sx={{
                        position: "absolute", top: -6, bottom: -6, width: 2, bgcolor: "#E57373",
                        left: `${posicion(cierreHorario)}%`
                    }}/>
                )}
            </Box>
        </Tooltip>
    );
};

const EstadoDia = ({jornada}) => {
    if (jornada.out_of_hours_s > 0) {
        return <Box component="span" sx={etiqueta(FUERA_FONDO, FUERA)}>+{duracion(jornada.out_of_hours_s)}</Box>;
    }
    if (jornada.unclosed > 0) return <Box component="span" sx={etiqueta("#FEF3C7", "#B45309")}>Sin cierre</Box>;
    if (!jornada.window) return <Box component="span" sx={etiqueta(PISTA, GRIS)}>Sin horario</Box>;
    return <Box component="span" sx={etiqueta("#DCFCE7", "#15803D")}>Dentro de horario</Box>;
};

const Tarjeta = ({titulo, valor, detalle, alerta}) => (
    <Card sx={{height: "100%", borderRadius: 3, boxShadow: "0 1px 3px rgba(16,24,40,.06)"}}>
        <CardContent sx={{p: 2.5, "&:last-child": {pb: 2.5}}}>
            <Typography sx={{
                textTransform: "uppercase", letterSpacing: 0.8, fontWeight: 700, fontSize: 13,
                color: alerta ? FUERA : GRIS
            }}>
                {titulo}
            </Typography>
            <Typography sx={{mt: 0.75, fontSize: 32, fontWeight: 700, lineHeight: 1.1, color: "#1F2937"}}>{valor}</Typography>
            {detalle && <Typography sx={{mt: 0.75, fontSize: 12, color: GRIS}}>{detalle}</Typography>}
        </CardContent>
    </Card>
);

const FilaChofer = ({chofer}) => {
    const [abierto, setAbierto] = useState(false);

    return (
        <>
            <TableRow hover onClick={() => setAbierto(!abierto)} sx={{
                cursor: "pointer", bgcolor: abierto ? FILA_ABIERTA : undefined,
                "&.MuiTableRow-hover:hover": {bgcolor: abierto ? FILA_ABIERTA : "#FAFAFC"},
                "& > td": {fontSize: 17, color: "#1F2937", borderBottomColor: "#EEF0F4", py: 2}
            }}>
                <TableCell sx={{fontWeight: 600}}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <IconButton size="small" sx={{color: GRIS}}>
                            {abierto ? <KeyboardArrowDownIcon fontSize="small"/> : <KeyboardArrowRightIcon fontSize="small"/>}
                        </IconButton>
                        <Box component="span" sx={{color: "primary.main", fontWeight: 600}}>{chofer.name}</Box>
                    </Stack>
                </TableCell>
                <TableCell align="right">{chofer.days}</TableCell>
                <TableCell align="right">{duracion(chofer.worked_s)}</TableCell>
                <TableCell align="right">{duracion(chofer.avg_s)}</TableCell>
                <TableCell align="right">
                    {chofer.out_of_hours_s > 0
                        ? <Box component="span" sx={etiqueta(FUERA_FONDO, FUERA)}>{duracion(chofer.out_of_hours_s)}</Box>
                        : "—"}
                </TableCell>
                <TableCell align="right">
                    {pct(chofer.vs_plan_pct)}
                    {chofer.vs_plan_pct !== null && chofer.vs_plan_pct !== undefined && (
                        <Typography sx={{fontSize: 12, color: GRIS}} display="block">
                            {chofer.plan_days} de {chofer.days} días con plan
                        </Typography>
                    )}
                </TableCell>
                <TableCell align="right" sx={{color: chofer.unclosed > 0 ? "warning.main" : undefined}}>
                    {chofer.unclosed || "—"}
                </TableCell>
            </TableRow>
            <TableRow>
                <TableCell colSpan={7} sx={{p: 0, borderBottom: abierto ? undefined : "none"}}>
                    <Collapse in={abierto} unmountOnExit>
                        <Box sx={{pl: 6, pr: 2, py: 1.5, bgcolor: "#FCFCFE"}}>
                            <Table size="small" sx={{
                                "& td": {fontSize: 16, color: "#1F2937", py: 1.75, borderBottomColor: "#EEF0F4"},
                                "& th": {...encabezado, py: 1.5}
                            }}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Día</TableCell>
                                        <TableCell>Inicio</TableCell>
                                        <TableCell>Término</TableCell>
                                        <TableCell sx={{width: "24%"}}>06:00 → 24:00</TableCell>
                                        <TableCell align="right">Duración</TableCell>
                                        <TableCell align="right">Plan</TableCell>
                                        <TableCell align="right">Vs plan</TableCell>
                                        <TableCell align="right">Entregas</TableCell>
                                        <TableCell sx={{width: "17%"}}>Viajes</TableCell>
                                        <TableCell/>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {chofer.workdays.map((jornada) => (
                                        <TableRow key={jornada.date}>
                                            <TableCell sx={{whiteSpace: "nowrap"}}>
                                                {fecha(jornada.date)}
                                            </TableCell>
                                            <TableCell>{hora(jornada.start_at)}</TableCell>
                                            <TableCell>{hora(jornada.end_at)}</TableCell>
                                            <TableCell><BarraDelDia jornada={jornada}/></TableCell>
                                            <TableCell align="right">{duracion(jornada.duration_s)}</TableCell>
                                            <TableCell align="right">
                                                {jornada.plan_s ? duracion(jornada.plan_s)
                                                    : <Box component="span" sx={etiqueta(PISTA, GRIS)}>Sin plan</Box>}
                                            </TableCell>
                                            <TableCell align="right">{pct(jornada.vs_plan_pct)}</TableCell>
                                            <TableCell align="right">{jornada.deliveries}</TableCell>
                                            <TableCell>
                                                {jornada.trips.map((t) => (
                                                    <Typography key={t.id} display="block" sx={{
                                                        fontSize: 14, lineHeight: 1.45, color: t.unclosed ? "#B45309" : GRIS
                                                    }}>
                                                        N° {codigo(t.code)} {hora(t.start_at)}–{hora(t.end_at)}
                                                        {t.closed_late ? " · cerrado días después" : ""}
                                                    </Typography>
                                                ))}
                                            </TableCell>
                                            <TableCell align="right"><EstadoDia jornada={jornada}/></TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    </Collapse>
                </TableCell>
            </TableRow>
        </>
    );
};

const DriverWorkdays = () => {
    // Por ahora solo superadmin; el backend tambien lo exige.
    const {authUser} = useJumboAuth();
    const navigate = useNavigate();
    const isSuperadmin = useMemo(
        () => (authUser?.profiles || []).some((profile) => profile.name === 'superadmin'),
        [authUser],
    );
    useEffect(() => {
        if (authUser && !isSuperadmin) navigate('/trips');
    }, [authUser, isSuperadmin, navigate]);

    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [descargando, setDescargando] = useState(false);
    const [desde, setDesde] = useState(moment().subtract(29, "days").format("YYYY-MM-DD"));
    const [hasta, setHasta] = useState(moment().format("YYYY-MM-DD"));
    const [driverId, setDriverId] = useState("");
    const [vehicleId, setVehicleId] = useState("");

    const filtros = {desde, hasta, driver_id: driverId, vehicle_id: vehicleId};

    const cargar = useCallback(() => {
        setLoading(true);
        setError(null);

        dashboardServices
            .getDriverWorkdays({desde, hasta, driver_id: driverId, vehicle_id: vehicleId})
            .then((res) => setData(res))
            .catch(() => setError("No se pudo cargar la jornada de choferes."))
            .finally(() => setLoading(false));
    }, [desde, hasta, driverId, vehicleId]);

    useEffect(() => {
        if (isSuperadmin) cargar();
    }, [cargar, isSuperadmin]);

    const exportar = async () => {
        setDescargando(true);
        try {
            const buffer = await dashboardServices.downloadDriverWorkdaysXlsx(filtros);
            downloadBuffer(buffer, `jornada_choferes_${desde}_${hasta}.xlsx`);
        } catch (e) {
            setError("No se pudo descargar el Excel.");
        } finally {
            setDescargando(false);
        }
    };

    const totales = data?.totals;

    return (
        <Box>
            <Typography sx={{fontSize: 28, fontWeight: 600, color: "#1F2937", mb: 0.75}}>Jornada de choferes</Typography>
            <Typography sx={{fontSize: 16, color: GRIS, mb: 3}}>
                Desde que el chofer inicia su primer viaje hasta que cierra su última entrega del día.
                Del {fecha(desde)} al {fecha(hasta)}
            </Typography>

            {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}

            {totales && (
                <Grid container spacing={2} sx={{mb: 3}}>
                    <Grid item xs={6} md={2.4}><Tarjeta titulo="Horas trabajadas" valor={duracion(totales.worked_s)}/></Grid>
                    <Grid item xs={6} md={2.4}><Tarjeta titulo="Promedio por jornada" valor={duracion(totales.avg_s)}/></Grid>
                    <Grid item xs={6} md={2.4}>
                        <Tarjeta titulo="Fuera de horario" alerta valor={totales.window_configured ? duracion(totales.out_of_hours_s) : "—"}
                                 detalle={totales.window_configured ? "Antes o después del horario del chofer" : "No hay horario configurado"}/>
                    </Grid>
                    <Grid item xs={6} md={2.4}><Tarjeta titulo="Jornadas" valor={totales.workdays}/></Grid>
                    <Grid item xs={12} md={2.4}>
                        <Tarjeta titulo="Viajes sin cierre" alerta={totales.unclosed > 0} valor={totales.unclosed}
                                 detalle="Siguen en curso después de su día"/>
                    </Grid>
                </Grid>
            )}

            <Card sx={{borderRadius: 3, boxShadow: "0 1px 3px rgba(16,24,40,.06)"}}>
                <CardContent sx={{p: 3.5}}>
                    <Stack direction={{xs: "column", md: "row"}} justifyContent="space-between"
                           alignItems={{xs: "stretch", md: "center"}} spacing={1.5} sx={{mb: 2}}>
                        <Typography sx={{fontSize: 21, fontWeight: 600, color: "#1F2937"}}>Por chofer</Typography>
                        <Stack direction={{xs: "column", sm: "row"}} spacing={1}>
                            <TextField size="small" type="date" label="Desde" value={desde}
                                       onChange={(e) => setDesde(e.target.value)} InputLabelProps={{shrink: true}}/>
                            <TextField size="small" type="date" label="Hasta" value={hasta}
                                       onChange={(e) => setHasta(e.target.value)} InputLabelProps={{shrink: true}}/>
                            <TextField size="small" select label="Chofer" value={driverId} sx={{minWidth: 180}}
                                       onChange={(e) => setDriverId(e.target.value)}>
                                <MenuItem value="">Todos los choferes</MenuItem>
                                {(data?.driver_options || []).map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
                            </TextField>
                            <TextField size="small" select label="Vehículo" value={vehicleId} sx={{minWidth: 150}}
                                       onChange={(e) => setVehicleId(e.target.value)}>
                                <MenuItem value="">Todos</MenuItem>
                                {(data?.vehicle_options || []).map((o) => <MenuItem key={o.id} value={o.id}>{o.name}</MenuItem>)}
                            </TextField>
                            <ExcelExportButton onClick={exportar} loading={descargando} disabled={loading}/>
                        </Stack>
                    </Stack>

                    {loading ? (
                        <Box sx={{display: "flex", justifyContent: "center", py: 6}}><CircularProgress/></Box>
                    ) : !data?.drivers?.length ? (
                        <Typography color="text.secondary" sx={{py: 4, textAlign: "center"}}>
                            No hay viajes iniciados en este rango.
                        </Typography>
                    ) : (
                        <TableContainer>
                            <Table sx={{"& thead th": encabezado}}>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>Chofer</TableCell>
                                        <TableCell align="right">Días</TableCell>
                                        <TableCell align="right">Horas totales</TableCell>
                                        <TableCell align="right">Promedio / día</TableCell>
                                        <TableCell align="right">Fuera de horario</TableCell>
                                        <TableCell align="right">Real vs plan</TableCell>
                                        <TableCell align="right">Sin cierre</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {data.drivers.map((chofer) => <FilaChofer key={chofer.driver_id} chofer={chofer}/>)}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default DriverWorkdays;
